import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import {
  IconChartColumn,
  IconEdit,
  IconInfo,
  IconOngoingCalls,
  IconPhoneBook,
} from '@shared/assets/images/svgs';
import { useMediaQuery, useTheme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { SidebarTabNames } from '../constants';
import { useNavigate } from 'react-router-dom';
import { SidebarTabNamesType } from '../types';
import { t } from 'i18next';
import { CustomTooltip } from '@shared/components/customTooltip';
import { CampaignType } from '@shared/services/apiService/apiService';
import { MorphingSubmenuPaths } from './MorphingSubmenuPaths';
import { SUBMENU_RAIL_WIDTH, roundBranchY } from './submenuPathGeometry';
import { RootState, useAppSelector } from '@shared/store';

interface MenuItem {
  title: SidebarTabNamesType;
  icon?: React.FunctionComponent;
  subMenuItems?: MenuItem[];
  disabled?: boolean;
}

interface EditCampaignSidebarProps {
  activeTab: SidebarTabNamesType;
  campaignType?: CampaignType;
  issuedTabs: SidebarTabNamesType[];
  tabIssueMessages: Partial<Record<SidebarTabNamesType, string>>;
  hasSquad?: boolean;
  onTabChange?: (tab: SidebarTabNamesType) => boolean;
  hideOnMobile?: boolean;
}

const menuItems: MenuItem[] = [
  {
    title: SidebarTabNames.EDIT_CAMPAIGN,
    icon: IconEdit,
    subMenuItems: [
      { title: SidebarTabNames.GENERAL },
      { title: SidebarTabNames.AGENT },
      { title: SidebarTabNames.KNOWLEDGE_BASE },
      { title: SidebarTabNames.SQUADS },
      { title: SidebarTabNames.SIP_TRUNK },
      { title: SidebarTabNames.SETTINGS },
      { title: SidebarTabNames.AMBIENCE_SETTINGS },
      { title: SidebarTabNames.NOISE_PROCESSING },
      { title: SidebarTabNames.CALL_ANALYSIS },
      { title: SidebarTabNames.POST_CALL_MESSAGES },
      { title: SidebarTabNames.CONVERSATION_SETTINGS },
    ],
  },
  {
    title: SidebarTabNames.PHONE_NUMBERS,
    icon: IconPhoneBook,
  },
  {
    title: SidebarTabNames.ONGOING_CALLS,
    icon: IconOngoingCalls,
  },
  {
    title: SidebarTabNames.STATISTICS,
    icon: IconChartColumn,
  },
];

const MENU_ICON_SIZE = 24;
/** Left offset for top-level nav rows (icon + label). */
const SIDEBAR_NAV_PADDING_LEFT = 6;
const MENU_LABEL_GAP = 16;
/** Submenu label starts inline with parent item labels (icon + gap). */
const SUBMENU_LABEL_PADDING_LEFT = MENU_ICON_SIZE + MENU_LABEL_GAP;
/** Submenu SVG spine is at x=1; center it under the parent nav icon. */
const SUBMENU_RAIL_LEFT = SIDEBAR_NAV_PADDING_LEFT + MENU_ICON_SIZE / 2 - 1;

const menuIconSx = (stroke: string) => ({
  minWidth: MENU_ICON_SIZE,
  width: MENU_ICON_SIZE,
  height: MENU_ICON_SIZE,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  margin: 0,
  '> svg': {
    width: MENU_ICON_SIZE,
    height: MENU_ICON_SIZE,
    display: 'block',
    flexShrink: 0,
  },
  '> svg > path': {
    stroke,
  },
});

const EditCampaignSidebar: React.FC<EditCampaignSidebarProps> = ({
  activeTab,
  issuedTabs,
  tabIssueMessages,
  campaignType,
  hasSquad = false,
  onTabChange,
  hideOnMobile = false,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const topbarHeight = useAppSelector((state: RootState) => state.customizer.TopbarHeight);
  const [isCollapsed, setIsCollapsed] = useState(isSmallScreen);
  const drawerWidth = isCollapsed ? 76 : 200;

  useEffect(() => {
    if (isSmallScreen) setIsCollapsed(true);
  }, [isSmallScreen]);

  const filteredMenuItems = useMemo(
    () =>
      menuItems
        .map((item) => {
          if (item.title === SidebarTabNames.EDIT_CAMPAIGN && item.subMenuItems) {
            let subMenuItems = item.subMenuItems;

            if (!hasSquad) {
              subMenuItems = subMenuItems.filter(
                (subItem) => subItem.title !== SidebarTabNames.SQUADS,
              );
            }

            return {
              ...item,
              subMenuItems,
            };
          }
          return item;
        })
        .filter(
          (item) => !(campaignType === 'inbound' && item.title === SidebarTabNames.PHONE_NUMBERS),
        ),
    [campaignType, hasSquad],
  );

  const findIndexes = useCallback(
    (
      tab: string,
      items: MenuItem[],
    ): {
      initMenuIndex: number;
      initSubmenuIndex: number;
    } => {
      for (let i = 0; i < items.length; i++) {
        if (items[i].title === tab) {
          return { initMenuIndex: i, initSubmenuIndex: -1 };
        }

        if (items[i].subMenuItems) {
          const subIndex = items[i].subMenuItems!.findIndex((sub) => sub.title === tab);
          if (subIndex !== -1) {
            return { initMenuIndex: i, initSubmenuIndex: subIndex };
          }
        }
      }
      return { initMenuIndex: 0, initSubmenuIndex: 0 };
    },
    [],
  );

  const [activeMenuIndex, setActiveMenuIndex] = useState<number>(0);
  const [activeSubmenuIndex, setActiveSubmenuIndex] = useState<number>(0);
  const railContainerRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLLIElement | null)[]>([]);
  const [branchYs, setBranchYs] = useState<number[]>([]);

  const measureBranchYs = useCallback((expectedCount: number) => {
    const container = railContainerRef.current;
    if (!container || expectedCount === 0) {
      setBranchYs([]);
      return;
    }

    const rows = rowRefs.current.slice(0, expectedCount);
    if (rows.length !== expectedCount || rows.some((el) => el === null)) {
      return;
    }

    const containerTop = container.getBoundingClientRect().top;
    const measured = rows.map((el) => {
      const rect = el!.getBoundingClientRect();
      return roundBranchY(rect.top + rect.height / 2 - containerTop);
    });

    setBranchYs(measured);
  }, []);

  useEffect(() => {
    const { initMenuIndex, initSubmenuIndex } = findIndexes(activeTab, filteredMenuItems);
    setActiveMenuIndex(initMenuIndex);
    setActiveSubmenuIndex(initSubmenuIndex);
  }, [activeTab, filteredMenuItems, findIndexes]);

  const navigateToTab = (tabName: SidebarTabNamesType) => {
    const canNavigate = !onTabChange || onTabChange(tabName);

    if (!canNavigate) return false;
    navigate(`${location.pathname}?tab=${encodeURIComponent(tabName)}`);
    if (isSmallScreen) setIsCollapsed(true);
    return true;
  };

  const onMenuItemSelect = (index: number) => {
    const menuItem = filteredMenuItems[index];

    if (menuItem.subMenuItems) {
      if (navigateToTab(menuItem.subMenuItems[0].title)) {
        setActiveMenuIndex(index);
        setActiveSubmenuIndex(0);
      }
    } else {
      if (navigateToTab(menuItem.title)) {
        setActiveMenuIndex(index);
        setActiveSubmenuIndex(-1);
      }
    }
  };

  const onSubMenuItemSelect = (index: number, parentIndex?: number) => {
    if (parentIndex !== undefined) {
      const submenu = filteredMenuItems[parentIndex].subMenuItems;
      if (submenu && submenu[index]) {
        if (!navigateToTab(submenu[index].title)) return;
      } else {
        if (!navigateToTab(filteredMenuItems[parentIndex].title)) return;
      }
      setActiveSubmenuIndex(index);
      setActiveMenuIndex(parentIndex);
    }
  };

  const getMenuItemColor = (index: number) => {
    return index === activeMenuIndex
      ? theme.palette.primary.main
      : theme.palette.extendedGrey?.neutral;
  };

  const editCampaignSubMenuCount =
    filteredMenuItems.find((item) => item.title === SidebarTabNames.EDIT_CAMPAIGN)?.subMenuItems
      ?.length ?? 0;

  useLayoutEffect(() => {
    if (isCollapsed) {
      setBranchYs([]);
      return;
    }

    rowRefs.current = rowRefs.current.slice(0, editCampaignSubMenuCount);
    measureBranchYs(editCampaignSubMenuCount);

    const container = railContainerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      measureBranchYs(editCampaignSubMenuCount);
    });

    observer.observe(container);
    rowRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [
    isCollapsed,
    editCampaignSubMenuCount,
    activeTab,
    issuedTabs,
    campaignType,
    hasSquad,
    measureBranchYs,
  ]);

  return (
    <Box
      sx={{
        display: { xs: hideOnMobile ? 'none' : 'flex', md: 'flex' },
        position: 'sticky',
        top: topbarHeight,
        alignSelf: 'flex-start',
      }}>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            position: 'relative',
            overflow: 'visible',
            // Stay above handoff canvas nodes that can otherwise paint into this column.
            zIndex: 1,
            transition: 'width 180ms ease',
          },
        }}
        variant="permanent"
        anchor="left">
        <IconButton
          size="small"
          aria-label={t(`campaignPage.editCampaign.${SidebarTabNames.EDIT_CAMPAIGN}`)}
          aria-expanded={!isCollapsed}
          onClick={() => setIsCollapsed((prev) => !prev)}
          sx={{
            position: 'absolute',
            top: 20,
            right: -14,
            zIndex: 2,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            '&:hover': {
              backgroundColor: theme.palette.background.paper,
            },
          }}>
          {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
        <List>
          {filteredMenuItems.map((item, menu_index) => (
            <React.Fragment key={'menu-' + menu_index}>
              <ListItem
                sx={{
                  paddingBottom: 0,
                  paddingLeft: 0,
                  paddingRight: 0,
                }}>
                <ListItemButton
                  data-testid={`campaign-sidebar-tab-${item.title}`}
                  onClick={() => onMenuItemSelect(menu_index)}
                  sx={{
                    paddingLeft: isCollapsed ? '12px' : `${SIDEBAR_NAV_PADDING_LEFT}px`,
                    paddingRight: '8px',
                    minHeight: 40,
                    alignItems: 'center',
                  }}>
                  <CustomTooltip
                    title={isCollapsed ? t(`campaignPage.editCampaign.${item.title}`) : ''}
                    placement="right"
                    arrow>
                    <ListItemIcon
                      sx={menuIconSx(getMenuItemColor(menu_index) ?? theme.palette.text.secondary)}>
                      {item.icon ? <item.icon /> : null}
                    </ListItemIcon>
                  </CustomTooltip>
                  {!isCollapsed && (
                    <ListItemText
                      sx={{
                        paddingLeft: `${MENU_LABEL_GAP}px`,
                        color: getMenuItemColor(menu_index),
                      }}
                      primary={t(`campaignPage.editCampaign.${item.title}`)}
                    />
                  )}
                  {!isCollapsed && issuedTabs.includes(item.title) && (
                    <CustomTooltip
                      title={
                        tabIssueMessages[item.title] ??
                        t('adminMainPage.errors.requiredFieldsNotFilled')
                      }
                      placement="top"
                      arrow>
                      <IconInfo />
                    </CustomTooltip>
                  )}
                </ListItemButton>
              </ListItem>
              {!isCollapsed && item.subMenuItems ? (
                <Box
                  ref={railContainerRef}
                  sx={{
                    position: 'relative',
                    paddingLeft: `${SIDEBAR_NAV_PADDING_LEFT}px`,
                  }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: SUBMENU_RAIL_LEFT,
                      width: SUBMENU_RAIL_WIDTH,
                      zIndex: 1,
                      pointerEvents: 'none',
                    }}>
                    <MorphingSubmenuPaths
                      branchYs={branchYs}
                      activeIndex={
                        menu_index === activeMenuIndex && activeSubmenuIndex >= 0
                          ? activeSubmenuIndex
                          : -1
                      }
                      activeColor={theme.palette.primary.main}
                    />
                  </Box>
                  {item.subMenuItems.map((subItem, sub_menu_index) => (
                    <List key={'sub-menu' + sub_menu_index} sx={{ padding: '0' }}>
                      <ListItem
                        key={subItem.title}
                        ref={(el) => {
                          rowRefs.current[sub_menu_index] = el;
                        }}
                        sx={{ padding: '0 16px 0 0' }}>
                        <ListItemButton
                          data-testid={`campaign-sidebar-tab-${subItem.title}`}
                          sx={{
                            padding: `6px 16px 6px ${SUBMENU_LABEL_PADDING_LEFT}px`,
                            minHeight: 36,
                          }}
                          onClick={() => onSubMenuItemSelect(sub_menu_index, menu_index)}>
                          <ListItemText
                            sx={{
                              margin: 0,
                              padding: 0,
                              color:
                                sub_menu_index === activeSubmenuIndex
                                  ? theme.palette.primary.main
                                  : theme.palette.extendedGrey?.neutral,
                              transition: 'color 0.3s ease',
                            }}
                            primary={t(`campaignPage.editCampaign.${subItem.title}`)}
                          />
                          {subItem.title && issuedTabs.includes(subItem.title) && (
                            <CustomTooltip
                              title={
                                tabIssueMessages[subItem.title] ??
                                t('adminMainPage.errors.requiredFieldsNotFilled')
                              }
                              placement="top"
                              arrow>
                              <IconInfo />
                            </CustomTooltip>
                          )}
                        </ListItemButton>
                      </ListItem>
                    </List>
                  ))}
                </Box>
              ) : null}
            </React.Fragment>
          ))}
        </List>
      </Drawer>
    </Box>
  );
};

export default EditCampaignSidebar;
